import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { PageHeader, DataToolbar, EmptyState } from '../../../shared-components/ui';
import { formatCurrency } from '../../../utils/CurrencyFormat';
import {
  CustomerReferral,
  getReferrals,
  updateReferralStatus,
} from '../../../services/request/CustomerReferrals';

const STATUSES = [
  { value: 'SUBMITTED', label: 'Submitted' },
  { value: 'IN_REVIEW', label: 'In review' },
  { value: 'ONBOARDED', label: 'Onboarded' },
  { value: 'DECLINED', label: 'Declined' },
];

function rowsFrom(response: any): CustomerReferral[] {
  const payload = response?.data ?? response;
  return payload?.result ?? [];
}

const ReferralsManagement: React.FC = () => {
  const [rows, setRows] = useState<CustomerReferral[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState<CustomerReferral | null>(null);
  const [status, setStatus] = useState('IN_REVIEW');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(rowsFrom(await getReferrals(statusFilter || undefined)));
    } catch (err) {
      console.error(err);
      setError('Failed to load customer referrals');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const open = (row: CustomerReferral) => {
    setSelected(row);
    setStatus(row.status);
    setNote(row.adminNote ?? '');
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await updateReferralStatus(selected.id, status, note);
      toast.success('Referral updated');
      setSelected(null);
      load();
    } catch (err) {
      console.error(err);
      toast.error('Failed to update referral');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <PageHeader
        eyebrow="Lending · Ops"
        title="Customer referrals"
        subtitle="Existing customers introduce someone new. Review the person, then onboard them from Customers."
        actions={
          <Button variant="outlined" startIcon={<Refresh />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        }
      />
      {loading ? <LinearProgress sx={{ mb: 2 }} /> : null}
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      <Card sx={{ mb: 3, p: 2 }}>
        <DataToolbar>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <MenuItem value="">All</MenuItem>
              {STATUSES.map((item) => (
                <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DataToolbar>
      </Card>
      {rows.length === 0 && !loading ? (
        <EmptyState title="No referrals" description="Referrals from the customer app will appear here." />
      ) : (
        <TableContainer component={Card}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Prospect</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Amount required</TableCell>
                <TableCell>Referred by</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.prospectName}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{row.prospectEmail}</Typography>
                    <Typography variant="body2" color="text.secondary">{row.prospectPhone}</Typography>
                  </TableCell>
                  <TableCell>{formatCurrency(row.amountRequired, 'INR', true)}</TableCell>
                  <TableCell>{row.referrerName || 'Existing customer'}</TableCell>
                  <TableCell><Chip size="small" label={row.status.replace('_', ' ')} /></TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => open(row)}>Review</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Dialog open={!!selected} onClose={() => setSelected(null)} fullWidth maxWidth="sm">
        <DialogTitle>Review referral</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2">
              Onboarded means you have created this person as a customer. Declined means you will not take them forward.
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={status} onChange={(event) => setStatus(event.target.value)}>
                {STATUSES.map((item) => (
                  <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              multiline
              minRows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReferralsManagement;
